'use client';

import { useState } from 'react';
import { Receipt, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useParentInvoices } from '@/hooks/use-invoices';
import { InvoiceList } from '@/components/parent/invoices/invoice-list';
import { formatCurrency } from '@/lib/format';

type FilterTab = 'all' | 'outstanding' | 'paid';

export default function ParentInvoicesPage() {
  const { data, isLoading, error } = useParentInvoices();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500">Failed to load invoices</p>
        </CardContent>
      </Card>
    );
  }

  const allInvoices = data?.invoices ?? [];
  const totalOutstanding = data?.totalOutstanding ?? 0;

  const filteredInvoices = allInvoices.filter(invoice => {
    if (activeTab === 'outstanding') {
      return invoice.status === 'sent' || invoice.status === 'overdue';
    }
    if (activeTab === 'paid') {
      return invoice.status === 'paid';
    }
    return true;
  });

  const outstandingCount = allInvoices.filter(
    inv => inv.status === 'sent' || inv.status === 'overdue'
  ).length;

  const paidCount = allInvoices.filter(inv => inv.status === 'paid').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Receipt className="w-8 h-8 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Invoices</h1>
          <p className="text-sm text-muted-foreground">{allInvoices.length} total invoices</p>
        </div>
      </div>

      {/* Outstanding Summary */}
      {totalOutstanding > 0 && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400">Total Outstanding</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(totalOutstanding)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeTab === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('all')}
        >
          All ({allInvoices.length})
        </Button>
        <Button
          variant={activeTab === 'outstanding' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('outstanding')}
          className={activeTab !== 'outstanding' ? 'text-amber-600' : ''}
        >
          <Clock className="w-4 h-4 mr-1" />
          Outstanding ({outstandingCount})
        </Button>
        <Button
          variant={activeTab === 'paid' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('paid')}
          className={activeTab !== 'paid' ? 'text-green-600' : ''}
        >
          <CheckCircle className="w-4 h-4 mr-1" />
          Paid ({paidCount})
        </Button>
      </div>

      {/* Invoices List */}
      <InvoiceList
        invoices={filteredInvoices}
        activeTab={activeTab}
        onViewAll={() => setActiveTab('all')}
      />
    </div>
  );
}
